import { useEffect, useRef, useState, type CSSProperties } from "react";
import { api, type PreviewPuzzle } from "../lib/api";
import { getUserId } from "../lib/identity";
import { connectSocket, getSocket } from "../lib/socket";
import { externalPuzzleUrl } from "../lib/puzzleUrl";
import { previewConfig, type PuzzleCopy } from "./config";
import { fitPuzzleFrame } from "./fitPuzzle";
import { tweakPuzzleFrame } from "./frameTweaks";
import { PuzzleBriefing } from "./PuzzleBriefing";
import { QrCode } from "./QrCode";
import { FastFamiliarLogo } from "./FastFamiliarLogo";

/**
 * Standalone puzzle preview. A splash screen lists the curated puzzles; picking
 * one parks a throwaway PREVIEW group on that stage (server) and loads the real
 * puzzle iframes in the show's styling. Solving it fires the server's
 * `hide_external_puzzle` over the socket, which returns you to the splash.
 *
 * All the wording on this screen — names, blurbs, intro, footer, QR destination
 * — comes from ./config.ts. Nothing user-facing should be typed in here.
 */

/** A puzzle the server will serve, paired with the copy we show for it. */
interface Listed {
  puzzle: PreviewPuzzle;
  copy: PuzzleCopy;
}

export function PreviewApp(): React.ReactElement {
  const [puzzles, setPuzzles] = useState<PreviewPuzzle[]>([]);
  const [active, setActive] = useState<Listed | null>(null);
  const [solved, setSolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const groupId = useRef(`PREVIEW-${crypto.randomUUID().slice(0, 8)}`).current;
  const userId = getUserId();

  // Load the puzzle list and join the preview room to hear completion events.
  useEffect(() => {
    void api.previewPuzzles().then((res) => {
      if (res.success === 1) setPuzzles(res.puzzles);
      else setError(res.error_message);
    });

    const socket = connectSocket();
    const join = () => socket.emit("join_room", groupId);
    if (socket.connected) join();
    // Re-join on every (re)connect so a dropped socket can still hear completion.
    socket.on("connect", join);
    // A correct answer (from either iframe) hides the external puzzle: solved.
    const onHide = () => setSolved(true);
    socket.on("hide_external_puzzle", onHide);

    return () => {
      socket.off("connect", join);
      socket.off("hide_external_puzzle", onHide);
    };
  }, [groupId]);

  // On solve, celebrate briefly then return to the menu.
  useEffect(() => {
    if (!solved) return;
    const t = window.setTimeout(() => {
      setSolved(false);
      setActive(null);
    }, 1800);
    return () => clearTimeout(t);
  }, [solved]);

  const play = async (entry: Listed) => {
    setError(null);
    const res = await api.previewSetup(groupId, entry.puzzle.key);
    if (res.success !== 1) {
      setError(res.error_message);
      return;
    }
    // Ensure we're in the room before the puzzle can be solved, so we reliably
    // hear the completion event even if the socket reconnected while idle.
    getSocket().emit("join_room", groupId);
    setSolved(false);
    setActive(entry);
  };

  if (active) {
    return (
      <PreviewStage
        entry={active}
        groupId={groupId}
        userId={userId}
        solved={solved}
        onBack={() => setActive(null)}
      />
    );
  }

  // Menu order comes from the config, not the server; anything the server won't
  // serve (or that has no copy written for it) is left out.
  const listed: Listed[] = previewConfig.puzzles.flatMap((copy) => {
    const puzzle = puzzles.find((p) => p.key === copy.key);
    return puzzle ? [{ puzzle, copy }] : [];
  });

  const theme = puzzles[0]?.theme ?? "LRCFS";
  const bannerUrl = `/api/get_file/${theme}/files/images/welcome-banner.jpeg`;

  return (
    <div className="preview-splash">
      <div className="preview-card">
        <img className="preview-banner" src={bannerUrl} alt="The Curse of the Burial Dagger" />
        <h1 className="preview-kicker">{previewConfig.kicker}</h1>
        {previewConfig.intro.map((para) => (
          <p key={para.slice(0, 40)} className="preview-intro">
            {para}
          </p>
        ))}
        {error && <p className="preview-error">{error}</p>}
        <ul className="preview-list">
          {listed.map((entry) => (
            <li key={entry.puzzle.key}>
              <button className="preview-item" onClick={() => void play(entry)}>
                <span className="preview-item__dot" aria-hidden />
                {entry.copy.name}
              </button>
            </li>
          ))}
        </ul>

        {/*
          Nothing down here is tappable on purpose — the QR code is the only way
          off this page, so the preview can sit running unattended without a
          passer-by navigating away from it.
        */}
        <div className="preview-brand">
          <div className="preview-brand__qr">
            <QrCode value={previewConfig.qr.url} size={116} />
          </div>
          <div className="preview-brand__aside">
            {previewConfig.qr.caption && (
              <p className="preview-brand__caption">{previewConfig.qr.caption}</p>
            )}
            <div className="preview-brand__credit">
              <FastFamiliarLogo className="preview-brand__logo" />
              <span className="preview-brand__name">{previewConfig.credit}</span>
            </div>
          </div>
        </div>

        <div className="preview-funders">
          <p className="preview-funders__text">{previewConfig.funders.text}</p>
          {previewConfig.funders.logos.length > 0 && (
            <div className="preview-funders__logos">
              {previewConfig.funders.logos.map((logo) => (
                <FunderLogo key={logo.src} src={logo.src} alt={logo.alt} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A funder's logo, hidden if its file isn't there yet. The artwork for these is
 * other organisations' trademarks and has to be their official asset, so the
 * page has to read correctly both before and after the files are dropped into
 * public/funders/ — a broken-image icon in a funding credit would be worse than
 * no logo at all.
 */
function FunderLogo({ src, alt }: { src: string; alt: string }): React.ReactElement | null {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img className="preview-funders__logo" src={src} alt={alt} onError={() => setFailed(true)} />
  );
}

function PreviewStage({
  entry,
  groupId,
  userId,
  solved,
  onBack,
}: {
  entry: Listed;
  groupId: string;
  userId: string;
  solved: boolean;
  onBack: () => void;
}): React.ReactElement {
  const { puzzle, copy } = entry;
  // Opens on the briefing (the scenario carries the numbers the puzzle needs),
  // and can be reopened while playing.
  const [briefing, setBriefing] = useState(copy.briefing.length > 0);
  // Clue text arrives over the socket, exactly as it does in the show.
  const [clueText, setClueText] = useState<string | null>(null);
  const [askingClue, setAskingClue] = useState(false);

  useEffect(() => {
    const socket = getSocket();
    const onClue = (payload: { text: string }) => setClueText(payload.text);
    socket.on("clue_disclosed", onClue);
    return () => {
      socket.off("clue_disclosed", onClue);
    };
  }, []);

  const askForClue = async () => {
    setAskingClue(true);
    const res = await api.askForClue(groupId);
    setAskingClue(false);
    // A granted clue arrives on the socket; a refusal ("no more clues") comes
    // back on the response, and the show puts that in front of the player too.
    if (res.success !== 1) setClueText(res.error_message);
  };
  const theme = puzzle.theme;
  const asset = (p: string) => `url(/api/get_file/${theme}/files/${p})`;
  const vars = {
    "--leather": asset("images/brown-leather.jpg"),
    "--paper": asset("images/paper-texture2.png"),
    "--fade": asset("cue-cards/fade.png"),
  } as CSSProperties;

  const primary = puzzle.filepath && externalPuzzleUrl(theme, puzzle.filepath, groupId, userId);
  const secondary =
    puzzle.secondary_filepath &&
    externalPuzzleUrl(theme, puzzle.secondary_filepath, groupId, userId);

  return (
    <div id="scaffold" className="preview-stage" style={vars}>
      <div className="preview-stage__bar">
        <button className="preview-back" onClick={onBack}>
          ← Menu
        </button>
        {copy.briefing.length > 0 && (
          <button className="preview-back" onClick={() => setBriefing(true)}>
            Briefing
          </button>
        )}
      </div>
      <div className="main_screen">
        <div className="top_element">
          <div className="content_col">
            <div id="content_area">
              {primary && (
                <iframe
                  id="external_puzzle_screen"
                  title={copy.name}
                  src={primary}
                  onLoad={(e) => {
                    // Fit first: the notes panel is positioned against the
                    // letterboxed picture, which is what fitting establishes.
                    fitPuzzleFrame(e.currentTarget, copy.reserve);
                    tweakPuzzleFrame(e.currentTarget, copy, "primary");
                  }}
                />
              )}
            </div>
            <div id="terminal_container">
              <div id="terminal_area">
                <div id="paper">
                  <button id="clue_button" onClick={() => void askForClue()} disabled={askingClue}>
                    Get a clue
                  </button>
                  {secondary && (
                    <iframe
                      id="secondary_external_puzzle_screen"
                      title={`${copy.name} — answer`}
                      src={secondary}
                      onLoad={(e) => tweakPuzzleFrame(e.currentTarget, copy, "secondary")}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {briefing && <PuzzleBriefing copy={copy} onStart={() => setBriefing(false)} />}
      {clueText && (
        <div className="overlay" onClick={() => setClueText(null)}>
          <div className="clue-card" onClick={(e) => e.stopPropagation()}>
            {/* Clue text is authored show content and carries markup, rendered
                as HTML exactly as the show's ClueModal does. */}
            <div dangerouslySetInnerHTML={{ __html: clueText }} />
            <button onClick={() => setClueText(null)}>Got it</button>
          </div>
        </div>
      )}
      {solved && (
        <div className="preview-solved">
          <div className="preview-solved__card">
            <p style={{ fontSize: 48, margin: 0 }}>✓</p>
            <h2>Well done!</h2>
            <p>Try another puzzle!</p>
          </div>
        </div>
      )}
    </div>
  );
}
